import { useInfiniteQuery } from '@tanstack/react-query';
import { getSiteEventsTimeline } from '../api/sitesApi';
import { useAuthStore } from '../../auth/stores/authStore';
import type { SiteEventsTimelineResponse } from '../types/siteTimeline';

export const siteTimelineKeys = {
  all: ['siteTimeline'] as const,
  lists: () => [...siteTimelineKeys.all, 'list'] as const,
  list: (siteId: number, isAuthenticated: boolean) =>
    [...siteTimelineKeys.lists(), { siteId, isAuthenticated }] as const,
};

const ITEMS_PER_PAGE = 20;

export interface UseSiteEventsTimelineReturn {
  events: SiteEventsTimelineResponse['items'];
  totalCount: number;
  hasMore: boolean;
  isLoading: boolean;
  isLoadingMore: boolean;
  error: Error | null;
  loadMore: () => void;
}

/**
 * Hook to fetch site events timeline with infinite scroll pagination
 */
export function useSiteEventsTimeline(
  siteId: number | null
): UseSiteEventsTimelineReturn {
  const isAuthenticated = useAuthStore(state => state.isAuthenticated);

  const {
    data,
    isLoading,
    isFetchingNextPage,
    hasNextPage,
    fetchNextPage,
    error,
  } = useInfiniteQuery({
    queryKey: siteTimelineKeys.list(siteId ?? 0, isAuthenticated),
    queryFn: async ({ pageParam = 0 }) => {
      if (!siteId) {
        throw new Error('siteId is required');
      }

      return await getSiteEventsTimeline(siteId, {
        skip: pageParam,
        take: ITEMS_PER_PAGE,
      });
    },
    getNextPageParam: lastPage => {
      return lastPage?.hasMore ? lastPage.skip + lastPage.take : undefined;
    },
    initialPageParam: 0,
    enabled: isAuthenticated && siteId !== null && siteId > 0,
    staleTime: 2 * 60 * 1000,
    placeholderData: previousData => previousData,
  });

  const events = data?.pages.flatMap(page => page?.items ?? []) ?? [];
  const totalCount = data?.pages[0]?.totalCount ?? 0;

  return {
    events,
    totalCount,
    hasMore: hasNextPage ?? false,
    isLoading,
    isLoadingMore: isFetchingNextPage,
    error: error as Error | null,
    loadMore: fetchNextPage,
  };
}
